import { stringToDate } from 'modules/core/lib/dates'

import type { CompensationChanges } from 'modules/bookkeepers/store/collections/compensationChanges'
import type CompensationChange from 'modules/bookkeepers/store/models/CompensationChange'
import type { ICompensationChange } from 'modules/bookkeepers/store/models/CompensationChange'
import { COMPENSATION_CHANGE_IDS } from 'modules/bookkeepers/store/models/CompensationChange'
import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import type { IncidenceChangeItem } from 'modules/payroll_bundle/lib/types'

/*
  Note: there is an issue with how we are calculating for new hires, since we
  cannot tell which are the original contract's compensations. That means that
  only the last "new" change is shown for new hires, which is not technically
  correct. We have a limitation based on the data coming in, which doesn't indicate
  when a compensation was originally added, since each time we version the contract
  we create new compensations to go along with it.
*/
const buildCompensationChanges = (
  incidenceTargetId: string | null,
  incidenceType: string | null,
  compensationChanges: CompensationChanges
): IncidenceChangeItem[] => {
  return compensationChanges.models.reduce((results: IncidenceChangeItem[], compensationChange) => {
    const previousCompensationFromCurrentContract = getPreviousCompensationChange(
      compensationChange,
      compensationChanges
    )

    const previousCompensationFromPreviousContract = matchCompensationFromPreviousContractVersion(
      compensationChange,
      compensationChanges
    )

    /**
     * Fallback from previous contract version
     */
    const previousCompensationChange =
      previousCompensationFromCurrentContract ?? previousCompensationFromPreviousContract

    if (!isEqual(compensationChange, previousCompensationChange)) {
      results.push({
        id: calculateChangeId(
          incidenceTargetId,
          incidenceType,
          compensationChange,
          previousCompensationChange
        ),
        effectiveAt: stringToDate(compensationChange.attributes.effective_at, 'backend_datetime'),
        currentValue: {
          ...compensationChange.toJS(),
        },
        previousValue: {
          ...(previousCompensationChange?.toJS() ?? {}),
        },
        changedBy: compensationChange.attributes.author,
      })
    }

    return results
  }, [])
}

const getPreviousCompensationChange = (
  compensationChange: CompensationChange,
  compensationChangesCollection: CompensationChanges
) => {
  const compensationChanges = compensationChangesCollection.getHistoryForCompensation(
    compensationChange.originalCompensationId
  )

  if (!compensationChanges) return null

  const sortedChanges = compensationChanges.sort((a, b) => {
    if (a.effectiveAt === b.effectiveAt) {
      return a.id > b.id ? 1 : 0
    } else {
      return a.effectiveAt > b.effectiveAt ? 1 : -1
    }
  })

  const currentCompensationIndex = sortedChanges.indexOf(compensationChange)

  return sortedChanges[currentCompensationIndex - 1] ?? null
}

const calculateChangeId = (
  incidenceTargetId: string | null,
  incidenceType: string | null,
  current: CompensationChange,
  previous: CompensationChange | null
) => {
  if (previous === null) {
    if (
      // Means we are viewing a compensation from the target contract version
      incidenceType === INCIDENCE_TYPES.contract &&
      current.attributes.contract_version_id == incidenceTargetId
    ) {
      return COMPENSATION_CHANGE_IDS.compensation_created
    }

    if (incidenceType === INCIDENCE_TYPES.hiring) {
      return COMPENSATION_CHANGE_IDS.compensation_created
    }

    return COMPENSATION_CHANGE_IDS.compensation_deleted
  }

  if (current.isDeleted) return COMPENSATION_CHANGE_IDS.compensation_deleted

  return COMPENSATION_CHANGE_IDS.compensation_updated
}

const matchCompensationFromPreviousContractVersion = (
  compensationChange: CompensationChange,
  compensationChangesCollection: CompensationChanges
) => {
  return (
    compensationChangesCollection.find((previousContractCompensation) => {
      const differentOriginalCompensationId =
        previousContractCompensation.attributes.original_compensation_id !==
        compensationChange.attributes.original_compensation_id

      return (
        differentOriginalCompensationId && isEqual(compensationChange, previousContractCompensation)
      )
    }) ?? null
  )
}

const IGNORED_KEYS_FOR_CHECKING = [
  'id',
  'compensation_id',
  'contract_version_id',
  'effective_at',
  'original_compensation_id',
]

type CompensationChangeKey = keyof ICompensationChange

const isEqual = (
  currentCompensation: CompensationChange,
  previousCompensation: CompensationChange | null
) => {
  if (!previousCompensation) return false

  const currentCompensationKeys = Object.keys(currentCompensation.attributes).filter(
    (key) => !IGNORED_KEYS_FOR_CHECKING.includes(key)
  )

  return currentCompensationKeys.every(
    (key) =>
      currentCompensation.get(key as CompensationChangeKey) ===
      previousCompensation.get(key as CompensationChangeKey)
  )
}

export default buildCompensationChanges
